import { gql } from '@apollo/client';
import { Button, Spinner, useToast } from '@chakra-ui/react';
import { useReportOrganisationCreateErrorsMutation } from './__generated__/ContactSupport.graphql';

export type ErrorDetail = {
  timestamp: string;
  errorMessage: string;
  fieldValues: {
    email: string;
    firstName: string;
    lastName: string;
    countryCode: string;
    organisationHandle: string;
    organisationDetail: {
      orgName: string;
      orgAddress?: string | null;
      dunsId?: string | null;
    };
  };
};

export const ContactSupport = ({
  errorDetails,
  resellerId,
  onClose,
}: {
  errorDetails: ErrorDetail[];
  resellerId: string;
  onClose: () => void;
}) => {
  const [sendErrors, { loading }] = useReportOrganisationCreateErrorsMutation();
  const toast = useToast();
  // ✅ Clean only allowed fields before sending
  const parseErrorDetails = (details: ErrorDetail[]) => {
    return details.map((e) => ({
      timestamp: e.timestamp,
      errorMessage: e.errorMessage,
      fieldValues: {
        email: e.fieldValues.email,
        firstName: e.fieldValues.firstName,
        lastName: e.fieldValues.lastName,
        countryCode: e.fieldValues.countryCode,
        organisationHandle: e.fieldValues.organisationHandle,
        organisationDetail: {
          orgName: e.fieldValues.organisationDetail.orgName,
          orgAddress: e.fieldValues.organisationDetail.orgAddress ?? null,
          dunsId: e.fieldValues.organisationDetail.dunsId ?? null,
        },
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      const cleaned = parseErrorDetails(errorDetails);

      const response = await sendErrors({
        variables: {
          uuid: resellerId,
          errorDetails: cleaned,
        },
      });

      onClose();
      if (response.errors) {
        toast({
          title: 'Error while contacting support',
          description: 'Could not contact support at the moment. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Email sent to Support Team',
        description:
          'An email has been sent to the support team and support team will revert back soon.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      console.log('Mutation response:', response.data);
    } catch (err) {
      console.error('Error sending support request:', err);
      toast({
        title: 'Error while contacting support',
        description: 'Could not contact support at the moment. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    }
  };

  return (
    <Button
      bg={'red.50'}
      borderColor={'red.100'}
      border={'1px'}
      _hover={{ bg: 'red.100' }}
      onClick={handleSubmit}
      isDisabled={loading}
      size={'sm'}
    >
      <span>Contact Support</span>
      {loading && <Spinner size={'sm'} ml={2} />}
    </Button>
  );
};

ContactSupport.graphql = {
  mutations: {
    ReportOrganisationCreateErrors: gql`
      mutation ReportOrganisationCreateErrors(
        $uuid: String!
        $errorDetails: [OrganisationCreateErrorInput!]!
      ) {
        reportOrganisationCreateErrors(uuid: $uuid, errorDetails: $errorDetails) {
          status
        }
      }
    `,
  },
};
